import React from 'react';
import { CheckCircle2, Plus, Send, Square } from 'lucide-react';

/** What the right-hand button does at this point of the visit */
export type VisitStage = 'end' | 'sign' | 'done' | 'none';

export interface VisitActionBarProps {
  stage: VisitStage;
  onAdd: () => void;
  /** Nothing can be added now (chart submitted, a form is open, read-only) */
  addDisabled: boolean;
  onEnd: () => void;
  onSign: () => void;
  onDone: () => void;
  busy?: boolean;
}

/** The stage of a visit for the action bar. Pure, so the workspace and tests agree. */
export function visitStage(opts: { hasVisit: boolean; inProgress: boolean; chartingStatus?: string | null; submitted: boolean; canEndHere: boolean }): VisitStage {
  if (!opts.hasVisit) return 'none';
  if (opts.submitted || (opts.chartingStatus && opts.chartingStatus !== 'DRAFT')) return 'done';
  if (opts.inProgress) return opts.canEndHere ? 'end' : 'none';
  return 'sign';
}

const STAGE: Record<Exclude<VisitStage, 'none'>, { label: string; className: string; icon: React.ReactNode }> = {
  end: { label: 'End visit', className: 'bg-red-700 hover:bg-red-800', icon: <Square size={18} aria-hidden="true" /> },
  sign: { label: 'Sign & submit', className: 'bg-slate-800 hover:bg-slate-700', icon: <Send size={18} aria-hidden="true" /> },
  done: { label: 'Done', className: 'bg-emerald-700 hover:bg-emerald-800', icon: <CheckCircle2 size={18} aria-hidden="true" /> },
};

/**
 * Phase 18U U2: the visit screen's sticky action bar, pinned above the safe area so a nurse
 * reaches Add and the next step (End visit → Sign & submit → Done) with one thumb, without
 * scrolling past the chart. Add is hidden once the chart is submitted.
 */
const VisitActionBar: React.FC<VisitActionBarProps> = ({ stage, onAdd, addDisabled, onEnd, onSign, onDone, busy = false }) => {
  const action = stage === 'none' ? null : STAGE[stage];
  const run = stage === 'end' ? onEnd : stage === 'sign' ? onSign : onDone;
  const showAdd = stage !== 'done';
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white px-4 pt-2 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      data-testid="visit-action-bar"
      data-stage={stage}
    >
      <div className="mx-auto flex max-w-3xl gap-2">
        {showAdd && (
          <button
            type="button"
            onClick={onAdd}
            disabled={addDisabled || busy}
            className="flex min-h-[52px] flex-[3] items-center justify-center gap-2 rounded-lg border-2 border-slate-800 bg-white text-base font-semibold text-slate-900 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
          >
            <Plus size={20} aria-hidden="true" />Add
          </button>
        )}
        {action && (
          <button
            type="button"
            onClick={run}
            disabled={busy}
            className={`flex min-h-[52px] flex-[2] items-center justify-center gap-2 rounded-lg text-base font-semibold text-white disabled:opacity-50 ${action.className}`}
          >
            {action.icon}{action.label}
          </button>
        )}
      </div>
    </div>
  );
};

export default VisitActionBar;
